import { useState, useMemo, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getMetricBreakdown } from "@/queries/retail";

interface MetricBreakdownModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportId: number | null;
  metricName: string | null;
  metricTitle: string | null;
  metricValue: number | null;
  stores: number[];
}

type DirectionFilter = "all" | "departures" | "arrivals";

export function MetricBreakdownModal({
  open,
  onOpenChange,
  reportId,
  metricName,
  metricTitle,
  metricValue,
  stores,
}: MetricBreakdownModalProps) {
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>("all");
  
  const { data: contributions, isLoading } = getMetricBreakdown(
    reportId,
    metricName,
    stores
  );

  useEffect(() => {
    if (!open) {
      setDirectionFilter("all");
    }
  }, [open]);

  const shouldShowDirectionFilter = 
    metricName === "total_flights" || metricName === "total_pax";

  const filteredContributions = useMemo(() => {
    if (!contributions || !Array.isArray(contributions)) return contributions;
    
    if (!shouldShowDirectionFilter || directionFilter === "all") return contributions;
    
    return contributions.filter((contrib: any) => {
      if (directionFilter === "departures") {
        return contrib.flight.direction === "departure";
      }
      return contrib.flight.direction === "arrival";
    });
  }, [contributions, directionFilter, shouldShowDirectionFilter]);

  const departureCount = useMemo(() => {
    if (!contributions || !Array.isArray(contributions)) return 0;
    return contributions.filter((c: any) => c.flight.direction === "departure").length;
  }, [contributions]);

  const arrivalCount = useMemo(() => {
    if (!contributions || !Array.isArray(contributions)) return 0;
    return contributions.filter((c: any) => c.flight.direction === "arrival").length;
  }, [contributions]);

  const measureMap: Record<string, string> = {
    total_flights: "Flights",
    service_flights: "Flights",
    remain_dep_flights: "Flights",
    remain_arr_flights: "Flights",
    current_flights: "Flights",
    hour_flights: "Flights",
    total_pax: "Passengers",
    service_pax: "Passengers",
    remaining_pax_dep: "Passengers",
    remaining_pax_arr: "Passengers",
    remain_pax: "Passengers",
    hour_pax: "Passengers",
    current_pax: "Passengers",
  };

  const explanationMap: Record<string, string> = {
    total_flights:
      "All scheduled flights in the terminal for the day.",
    total_pax:
      "All arriving and departing passengers in the terminal for the day.",
    service_flights:
      "All departing flights assigned to the selected store's service area.",
    remain_dep_flights:
      "Departure flights in the service area that have not yet departed.",
    remain_arr_flights:
      "Arrival flights at gates in this service area that have not yet arrived.",
    remaining_pax_dep:
      "Passengers tied to remaining departure flights who have not yet entered the service area.",
    remaining_pax_arr:
      "Arriving passengers at gates in this service area that have not yet arrived.",
    remain_pax:
      "All passengers at this terminal that have not yet departed.",
    service_pax:
      "Full passenger totals associated with the departing flights in the selected service area.",
    current_flights:
      "Departure flights from gates in this service area currently active.",
    hour_flights:
      "Departure flights the store will experience in the next rolling 60 minutes.",
    hour_pax:
      "Passengers expected to enter the service area in the next rolling 60 minutes.",
    current_pax:
      "Passengers physically present in the service area right now.",
  };

  const suffixText = metricName ? measureMap[metricName] || "Items" : "Items";
  const explanation = metricName ? explanationMap[metricName] : null;

  const flightCountMetrics = [
    "total_flights",
    "service_flights",
    "remain_dep_flights",
    "remain_arr_flights",
    "current_flights",
    "hour_flights",
  ];

  const shouldShowValue = metricName
    ? !flightCountMetrics.includes(metricName)
    : true;

  const total =
    filteredContributions && Array.isArray(filteredContributions)
      ? filteredContributions.reduce(
          (sum: number, flight: any) => sum + flight.value,
          0
        )
      : 0;

  const internationalTotal =
    filteredContributions && Array.isArray(filteredContributions)
      ? filteredContributions.reduce(
          (sum: number, flight: any) =>
            sum + (flight.flight.international ? flight.value : 0),
          0
        )
      : 0;

  const domesticTotal =
    filteredContributions && Array.isArray(filteredContributions)
      ? filteredContributions.reduce(
          (sum: number, flight: any) =>
            sum + (!flight.flight.international ? flight.value : 0),
          0
        )
      : 0;

  const internationalPercentage =
    total > 0 ? (internationalTotal / total) * 100 : 0;
  const domesticPercentage = total > 0 ? (domesticTotal / total) * 100 : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {shouldShowDirectionFilter && directionFilter !== "all"
              ? directionFilter === "departures"
                ? `Departure ${metricTitle || "Flights"}`
                : `Arrival ${metricTitle || "Flights"}`
              : metricTitle || "Metric Breakdown"
            }
            {metricValue !== null && (!shouldShowDirectionFilter || directionFilter === "all") && (
              <span className="text-slate-400 font-normal ml-2">
                ({metricValue.toLocaleString()})
              </span>
            )}
            {shouldShowDirectionFilter && directionFilter !== "all" && (
              <span className="text-slate-400 font-normal ml-2">
                ({filteredContributions?.length || 0} flights)
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        
        {shouldShowDirectionFilter && (
          <div className="flex items-center justify-center gap-0.5 sm:gap-1 p-0.5 sm:p-1 bg-slate-800/50 rounded-lg border border-slate-700/50 w-fit mx-auto mb-4">
            <button
              onClick={() => setDirectionFilter("all")}
              className={`px-3 sm:px-6 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 ${
                directionFilter === "all"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-slate-400 hover:text-white hover:bg-slate-700/50"
              }`}
            >
              All
              {contributions && (
                <span className="ml-1 sm:ml-1.5 text-[10px] sm:text-xs opacity-75">
                  ({contributions.length})
                </span>
              )}
            </button>
            <button
              onClick={() => setDirectionFilter("departures")}
              className={`px-3 sm:px-6 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 ${
                directionFilter === "departures"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-slate-400 hover:text-white hover:bg-slate-700/50"
              }`}
            >
              Departures
              <span className="ml-1 sm:ml-1.5 text-[10px] sm:text-xs opacity-75">
                ({departureCount})
              </span>
            </button>
            <button
              onClick={() => setDirectionFilter("arrivals")}
              className={`px-3 sm:px-6 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 ${
                directionFilter === "arrivals"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-slate-400 hover:text-white hover:bg-slate-700/50"
              }`}
            >
              Arrivals
              <span className="ml-1 sm:ml-1.5 text-[10px] sm:text-xs opacity-75">
                ({arrivalCount})
              </span>
            </button>
          </div>
        )}
        
        <ScrollArea className="h-[calc(100vh-380px)]">
          <div className="pr-4">
            {explanation && (
              <div className="text-sm text-slate-400 italic mb-4 pb-3 border-b border-slate-700/50">
                {explanation}
              </div>
            )}
          {isLoading && <div>Loading...</div>}
          {filteredContributions &&
          Array.isArray(filteredContributions) &&
          filteredContributions.length > 0 ? (
            <div>
              {filteredContributions.map((contrib: any, idx: number) => (
                <div
                  key={contrib.flight.id ?? `idx-${idx}`}
                  className={`flex justify-between items-start py-3 border-t border-slate-700/50 ${
                    shouldShowDirectionFilter && contrib.flight.direction === "departure" 
                      ? "border-l-2 border-l-emerald-500/50 pl-3" 
                      : shouldShowDirectionFilter && contrib.flight.direction === "arrival"
                      ? "border-l-2 border-l-sky-500/50 pl-3"
                      : ""
                  }`}
                >
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      {contrib.flight.carrier_image && (
                        <img
                          src={contrib.flight.carrier_image}
                          className="w-6 h-6"
                          onError={(e) =>
                            (e.currentTarget.style.display = "none")
                          }
                          alt=""
                        />
                      )}
                      <span className="font-semibold text-base">
                        {contrib.flight.number}
                      </span>
                      <Badge variant={"secondary"} className="text-xs">
                        {contrib.flight.international
                          ? "INTERNATIONAL"
                          : "DOMESTIC"}
                      </Badge>
                    </div>
                    <div className="text-sm text-slate-400">
                      {contrib.flight.dep_iata} → {contrib.flight.arr_iata}
                      {contrib.flight.gate && (
                        <span className="ml-2">
                          • Gate{" "}
                          <span className="font-medium">
                            {contrib.flight.gate}
                          </span>
                        </span>
                      )}
                      {(contrib.flight.direction === "arrival" ? contrib.flight.sch_arr_time : contrib.flight.sch_dep_time) && (
                        <span className="ml-2">
                          • {contrib.flight.direction === "arrival" ? contrib.flight.sch_arr_time : contrib.flight.sch_dep_time}
                        </span>
                      )}
                    </div>
                  </div>
                  {shouldShowValue && (
                    <span className="font-medium text-base">
                      {Math.round(contrib.value)} {suffixText}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : filteredContributions &&
            Array.isArray(filteredContributions) &&
            filteredContributions.length === 0 ? (
            <div className="text-slate-400">
              No flight contributions for this metric
            </div>
          ) : null}
          </div>
        </ScrollArea>

        {filteredContributions && Array.isArray(filteredContributions) && filteredContributions.length > 0 && (
          <div className="pt-3 border-t border-slate-700">
            <div className="flex justify-between items-center">
              <span className="font-bold text-lg">Total</span>
              <span className="font-bold text-lg">
                {Math.round(total)} {suffixText}
              </span>
            </div>
            <div className="mt-3 space-y-2 pt-2 border-t border-slate-700/50">
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-400">International</span>
                <span className="font-medium">
                  {Math.round(internationalTotal)} {suffixText}
                  <span className="text-slate-400 ml-2">
                    ({internationalPercentage.toFixed(1)}%)
                  </span>
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-400">Domestic</span>
                <span className="font-medium">
                  {Math.round(domesticTotal)} {suffixText}
                  <span className="text-slate-400 ml-2">
                    ({domesticPercentage.toFixed(1)}%)
                  </span>
                </span>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
